import React, { useState } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Target, Eye, Star, Shield, Heart, Layers, X } from 'lucide-react';

// Placeholder for company logo - replace src with actual logo path
const COMPANY_LOGO = '/logo-aretina.png';

// Institutional content - Official content
const INSTITUTIONAL_CONTENT = {
  mission: {
    title: 'Misión',
    icon: Target,
    content: 'Entregamos productos únicos con calidad y confort para cautivar a nuestros clientes, garantizando una experiencia de compra memorable.'
  },
  vision: {
    title: 'Visión',
    icon: Eye,
    content: 'Ser el proveedor de primera elección de nuestros clientes, fortaleciendo nuestras marcas y creciendo juntos un 30%.'
  },
  values: [
    {
      title: 'Excelencia',
      icon: Star,
      content: 'Hago las cosas bien a la primera, con atención, calidad y buscando siempre nuevas formas de aportar valor.'
    },
    {
      title: 'Integridad',
      icon: Shield,
      content: 'Soy consciente entre lo que pienso, digo y hago. Cuido de mis acciones, reconozco mis errores y busco soluciones.'
    },
    {
      title: 'Actitud de Servicio',
      icon: Heart,
      content: 'Ayudo sin esperar nada a cambio y escucho con atención para servir mejor.'
    }
  ]
};

// Color map for flashcard backs
const CARD_COLORS = {
  'Misión': 'bg-red-500',
  'Visión': 'bg-amber-500',
  'Excelencia': 'bg-emerald-500',
  'Integridad': 'bg-blue-500',
  'Actitud de Servicio': 'bg-violet-500',
};

const FlipCard = ({ title, icon: Icon, content, flashMode, revealed, onReveal, large }) => {
  const flipped = !flashMode || revealed;
  const colorClass = CARD_COLORS[title] || 'bg-slate-500';

  return (
    <div
      className={`${flashMode && !revealed ? 'cursor-pointer' : ''}`}
      style={{ perspective: '800px' }}
      onClick={() => { if (flashMode && !revealed) onReveal(); }}
      data-testid={`flip-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div
        style={{
          position: 'relative',
          minHeight: flashMode ? '180px' : undefined,
          transition: 'transform 400ms ease-in-out',
          transformStyle: 'preserve-3d',
          transform: flipped ? 'rotateY(0deg)' : 'rotateY(180deg)',
        }}
      >
        {/* Front face — in normal flow to set height */}
        <div style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden', minHeight: flashMode ? '180px' : undefined }}>
          <Card className="bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden" style={{ minHeight: flashMode ? '180px' : undefined }}>
            <CardContent className={large ? 'p-6' : 'p-5'}>
              <div className={`flex items-center gap-3 ${large ? 'mb-4' : 'mb-3'}`}>
                <div className={`${large ? 'w-12 h-12 rounded-xl' : 'w-10 h-10 rounded-lg'} bg-red-50 flex items-center justify-center flex-shrink-0`}>
                  <Icon className={`${large ? 'w-6 h-6' : 'w-5 h-5'} text-red-600`} />
                </div>
                <h3 className={`${large ? 'text-xl' : 'text-lg'} font-bold text-slate-800 font-['Manrope']`}>{title}</h3>
              </div>
              <p className={`${large ? 'text-slate-600' : 'text-slate-500 font-light'} text-sm leading-relaxed overflow-hidden`}>{content}</p>
            </CardContent>
          </Card>
        </div>

        {/* Back face — absolute overlay, same height as front */}
        <div style={{ position: 'absolute', inset: 0, backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}>
          <Card className={`h-full ${colorClass} border-0 shadow-md`}>
            <CardContent className="h-full flex items-center justify-center p-6">
              <Icon className="w-12 h-12 text-white/90" />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

const Inicio = () => {
  const [logoError, setLogoError] = React.useState(false);
  const [flashMode, setFlashMode] = useState(false);
  const [revealed, setRevealed] = useState({});

  const enterFlashMode = () => {
    setRevealed({});
    setFlashMode(true);
  };

  const exitFlashMode = () => {
    setFlashMode(false);
    setRevealed({});
  };

  const revealCard = (key) => {
    setRevealed(prev => ({ ...prev, [key]: true }));
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto space-y-8" data-testid="inicio-page">
        {/* Logo Section - Larger and cleaner */}
        <div className="flex flex-col items-center pt-2 pb-4">
          {!logoError ? (
            <div className="flex flex-col items-center">
              <img
                src={COMPANY_LOGO}
                alt="Aretina"
                className="h-28 sm:h-36 md:h-44 lg:h-52 w-auto object-contain"
                onError={() => setLogoError(true)}
                data-testid="company-logo"
              />
            </div>
          ) : (
            // Fallback logo if image doesn't load
            <div className="flex flex-col items-center gap-3" data-testid="company-logo-fallback">
              <div className="w-24 h-24 sm:w-32 sm:h-32 bg-gradient-to-br from-red-600 to-red-700 rounded-2xl flex items-center justify-center shadow-lg">
                <span className="text-white text-4xl sm:text-5xl font-bold font-['Manrope']">A</span>
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-red-600 font-['Manrope']">
                aretina
              </h1>
            </div>
          )}
          
          {/* Subtitle */}
          <p className="text-slate-500 text-sm sm:text-base mt-2 text-center max-w-md">
            Plataforma integral de gestión empresarial
          </p>
        </div>

        {/* Divider + FlashCards button */}
        <div className="flex items-center justify-center relative">
          <div className="h-px w-20 bg-red-200"></div>
          <div className="w-2 h-2 bg-red-500 rounded-full mx-4"></div>
          <div className="h-px w-20 bg-red-200"></div>
          <div className="absolute right-0">
            {flashMode ? (
              <Button
                variant="outline"
                size="sm"
                onClick={exitFlashMode}
                className="text-xs"
                data-testid="exit-flashcards-btn"
              >
                <X className="w-3.5 h-3.5 mr-1.5" />
                Salir de FlashCards
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={enterFlashMode}
                className="text-xs"
                data-testid="flashcards-btn"
              >
                <Layers className="w-3.5 h-3.5 mr-1.5" />
                FlashCards
              </Button>
            )}
          </div>
        </div>

        {/* Mission and Vision Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FlipCard
            title={INSTITUTIONAL_CONTENT.mission.title}
            icon={INSTITUTIONAL_CONTENT.mission.icon}
            content={INSTITUTIONAL_CONTENT.mission.content}
            flashMode={flashMode}
            revealed={revealed['mission']}
            onReveal={() => revealCard('mission')}
            large
          />
          <FlipCard
            title={INSTITUTIONAL_CONTENT.vision.title}
            icon={INSTITUTIONAL_CONTENT.vision.icon}
            content={INSTITUTIONAL_CONTENT.vision.content}
            flashMode={flashMode}
            revealed={revealed['vision']}
            onReveal={() => revealCard('vision')}
            large
          />
        </div>

        {/* Values Section Header */}
        <div className="text-center pt-4">
          <h2 className="text-2xl font-bold text-slate-800 font-['Manrope']">
            Nuestros Valores
          </h2>
          <p className="text-slate-500 text-sm mt-1">
            Los principios que guían nuestras acciones
          </p>
        </div>

        {/* Values Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {INSTITUTIONAL_CONTENT.values.map((value, index) => (
            <FlipCard
              key={index}
              title={value.title}
              icon={value.icon}
              content={value.content}
              flashMode={flashMode}
              revealed={revealed[`value_${index}`]}
              onReveal={() => revealCard(`value_${index}`)}
            />
          ))}
        </div>

        {/* Footer note */}
        <div className="text-center pt-6 pb-8">
          <p className="text-slate-400 text-xs">
            © {new Date().getFullYear()} Aretina. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default Inicio;
